import { useNavigate, useParams, Link, Navigate } from "react-router-dom";
import { useSim } from "@/context/SimContext";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";
import { useBilling } from "@/context/BillingContext";
import { SECTION_BY_ID, SIM_BY_ID, SIM_ROUTES, isSimNew } from "@/data/simulators";
import { Button } from "@/components/ui/button";
import { BrandLogo } from "@/components/BrandLogo";
import {
  ArrowRight, ChevronLeft, ChevronDown, LogOut, LogIn,
  GraduationCap, Microscope, Lock, Shield, Users, BookOpen, Clock, Sparkles,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { SUBSCRIPTIONS_ENABLED } from "@/lib/featureFlags";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtEta = (val) => {
  if (typeof val !== "string") return null;
  const m = val.match(/^(\d{4})-(\d{2})$/);
  if (!m) return null;
  const mo = MONTHS[parseInt(m[2], 10) - 1];
  return mo ? `${mo} ${m[1]}` : null;
};

export default function SectionPage() {
  const navigate = useNavigate();
  const { sectionId } = useParams();
  const { setActiveSim, reset } = useSim();
  const { user, authReady, logout } = useAuth();
  const { isSimVisible, isSimFree, getSimEta } = useContent();
  const { entitlement, billingReady } = useBilling();
  const isAdmin = user?.role === "admin";
  // Wait for billingReady before flagging a signed-in user as "restricted" — otherwise
  // a paying user briefly sees "Pro/locked" badges before their entitlement loads.
  // Signed-out users are always restricted (no waiting needed).
  // When SUBSCRIPTIONS_ENABLED is false nobody is restricted — every sim is free.
  const restricted = SUBSCRIPTIONS_ENABLED
    && !isAdmin
    && (!user || (billingReady && entitlement?.access === "free"));

  const section = SECTION_BY_ID[sectionId];
  if (!section) return <Navigate to="/sections" replace />;

  const Icon = section.Icon;
  const sims = section.sims.map((id) => SIM_BY_ID[id]);

  const launch = (id) => {
    reset();
    setActiveSim(id);
    const path = SIM_ROUTES[id] || "/intake";
    // Anon users are allowed on FREE sims (no login prompt). Only redirect to
    // login when the sim requires it (SimGate/Paywall then handles the rest).
    // When SUBSCRIPTIONS_ENABLED is false every sim is free — no login required.
    if (SUBSCRIPTIONS_ENABLED && !user && !isSimFree(id)) {
      navigate(`/login?next=${encodeURIComponent(path)}`);
      return;
    }
    navigate(path);
  };

  return (
    <div className="min-h-screen bg-[#F4F7F9]">
      <header className="border-b border-slate-200 bg-white/70 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-3">
          <Link to="/sections" className="flex items-center gap-3 group" data-testid="brand-logo-home">
            <BrandLogo chip />
            <span className="hidden sm:block text-[11px] text-slate-500 border-l border-slate-200 pl-3">
              Veterinary clinical simulation training
            </span>
          </Link>
          {authReady && (
            <div className="flex items-center gap-2" data-testid="section-auth-controls">
              {user ? (
                <>
                  {user.role === "admin" && (
                    <Link to="/admin" data-testid="section-nav-admin-btn">
                      <Button variant="outline" size="sm" className="text-[#0047AB] border-[#0047AB]/40">
                        <Shield className="w-4 h-4 mr-1.5" />
                        <span className="hidden sm:inline">Admin</span>
                      </Button>
                    </Link>
                  )}
                  {user.role === "instructor" && (
                    <Link to="/team" data-testid="section-nav-team-btn">
                      <Button variant="outline" size="sm" className="text-[#0047AB] border-[#0047AB]/40">
                        <Users className="w-4 h-4 mr-1.5" />
                        <span className="hidden sm:inline">My team</span>
                      </Button>
                    </Link>
                  )}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid="section-user-menu-trigger"
                        className="text-slate-700 gap-2 pl-1.5 pr-2 hover:bg-slate-100"
                      >
                        <span className="w-7 h-7 rounded-full bg-[#0047AB]/10 text-[#0047AB] flex items-center justify-center text-xs font-bold uppercase">
                          {(user.name || user.email || "?").trim().charAt(0)}
                        </span>
                        <span className="hidden md:inline max-w-[160px] truncate text-sm" data-testid="section-user-name">
                          {user.name || user.email}
                        </span>
                        <ChevronDown className="w-4 h-4 opacity-60" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-60" data-testid="section-user-menu">
                      <DropdownMenuLabel className="font-normal">
                        <div className="text-sm font-semibold text-slate-900 truncate">{user.name || "Signed in"}</div>
                        <div className="text-[11px] text-slate-500 truncate">{user.email}</div>
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => navigate("/dashboard")}
                        data-testid="section-nav-dashboard-btn"
                        className="cursor-pointer"
                      >
                        <GraduationCap className="w-4 h-4 mr-2 text-[#0047AB]" />
                        My scores &amp; sessions
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild className="cursor-pointer">
                        <a href="/CertIVSims-Learner-Guide.pdf" download data-testid="section-nav-learner-guide-btn">
                          <BookOpen className="w-4 h-4 mr-2 text-[#0047AB]" />
                          Quick-start guide (PDF)
                        </a>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => logout()}
                        data-testid="section-logout-btn"
                        className="cursor-pointer text-slate-600"
                      >
                        <LogOut className="w-4 h-4 mr-2" />
                        Sign out
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <Link to="/login" data-testid="nav-login-btn">
                  <Button size="sm" className="bg-[#0047AB] hover:bg-[#003380] text-white">
                    <LogIn className="w-4 h-4 mr-1.5" /> Sign in
                  </Button>
                </Link>
              )}
            </div>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10 fade-up">
        <Button
          variant="ghost"
          onClick={() => navigate("/sections")}
          data-testid="section-back-btn"
          className="text-slate-500 mb-4 -ml-2"
        >
          <ChevronLeft className="w-4 h-4 mr-1" /> All sections
        </Button>

        <div className="flex items-center gap-4">
          <div
            className="w-14 h-14 rounded-xl flex items-center justify-center text-white shrink-0"
            style={{ background: section.accent }}
          >
            <Icon className="w-7 h-7" />
          </div>
          <div>
            <div
              className="text-[11px] uppercase tracking-[0.18em] font-semibold"
              style={{ color: section.accent }}
            >
              Section {String(section.n).padStart(2, "0")}
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900" data-testid="section-title">
              {section.title}
            </h1>
          </div>
        </div>
        <p className="text-base text-slate-600 mt-3 max-w-2xl">{section.blurb}</p>

        {sims.length === 0 ? (
          <div
            className="mt-10 border-2 border-dashed border-slate-200 rounded-xl py-16 text-center"
            data-testid="section-empty"
          >
            <Microscope className="w-10 h-10 text-slate-300 mx-auto" />
            <div className="text-lg font-semibold text-slate-700 mt-3">
              Coming soon
            </div>
            <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
              There are no simulators available in this section just yet. Check back soon —
              we&apos;re adding new clinical drills regularly.
            </p>
            <Button
              onClick={() => navigate("/sections")}
              className="mt-5 bg-[#0047AB] hover:bg-[#003380] text-white"
            >
              Back to all sections
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
            {sims.map(({ id, title, blurb, tags, Art, Icon: SimIcon }) => {
              const soon = !isSimVisible(id);
              const comingSoon = soon && !isAdmin;
              const locked = !soon && restricted && !isSimFree(id);
              const greyed = comingSoon || locked;
              const showNew = !soon && isSimNew(id);
              const etaLabel = soon ? fmtEta(getSimEta(id)) : null;
              const handleClick = () => {
                if (comingSoon || locked) {
                  // Anon users hitting a Pro tile: prompt sign-in, then send them
                  // to pricing so they can complete the upgrade in one flow.
                  if (locked) navigate(user ? "/pricing" : "/login?next=/pricing");
                  return;
                }
                launch(id);
              };
              const unclickable = comingSoon && !restricted;
              return (
              <button
                key={id}
                type="button"
                onClick={handleClick}
                aria-disabled={unclickable}
                data-testid={`sim-tile-${id}`}
                className={`group text-left bg-white border rounded-xl overflow-hidden transition-all duration-300 ${
                  unclickable
                    ? "border-slate-200 cursor-not-allowed"
                    : greyed
                    ? "border-slate-200 hover:border-slate-300 hover:shadow-md"
                    : "border-slate-200 hover:border-[#0047AB] hover:shadow-xl hover:-translate-y-1"
                }`}
              >
                <div className={`h-44 w-full overflow-hidden relative ${greyed ? "opacity-50 grayscale" : ""}`}>
                  <Art />
                  {showNew && (
                    <span
                      className="absolute top-3 left-3 inline-flex items-center gap-1 bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full shadow"
                      data-testid={`sim-new-badge-${id}`}
                    >
                      <Sparkles className="w-3 h-3" /> New
                    </span>
                  )}
                  {soon ? (
                    <>
                      {!isAdmin && <span className="absolute inset-0 bg-slate-900/10" />}
                      <span className="absolute top-3 right-3 inline-flex items-center gap-1 bg-amber-500 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full shadow" data-testid={`sim-soon-badge-${id}`}>
                        <Clock className="w-3 h-3" /> Coming soon
                      </span>
                    </>
                  ) : locked ? (
                    <>
                      <span className="absolute inset-0 bg-slate-900/10" />
                      <span className="absolute top-3 right-3 inline-flex items-center gap-1 bg-[#0047AB] text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full shadow" data-testid={`sim-pro-badge-${id}`}>
                        <Lock className="w-3 h-3" /> Pro
                      </span>
                    </>
                  ) : (restricted && isSimFree(id)) ? (
                    <span className="absolute top-3 right-3 bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full shadow" data-testid={`sim-free-badge-${id}`}>
                      Free
                    </span>
                  ) : null}
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-7 h-7 rounded-md bg-[#0047AB]/10 flex items-center justify-center">
                      <SimIcon className="w-4 h-4 text-[#0047AB]" />
                    </div>
                    <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed">{blurb}</p>
                  <div className="flex flex-wrap gap-2 mt-4">
                    {tags.map((t) => (
                      <span
                        key={t}
                        className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded bg-slate-100 text-slate-600"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                  {comingSoon ? (
                    <div className="flex items-center gap-1.5 mt-5 text-sm font-semibold text-amber-600" data-testid={`sim-soon-cta-${id}`}>
                      <Clock className="w-4 h-4" />
                      Coming soon{etaLabel ? ` · ${etaLabel}` : ""}
                    </div>
                  ) : locked ? (
                    <div className="flex items-center gap-1.5 mt-5 text-sm font-semibold text-slate-500" data-testid={`sim-locked-cta-${id}`}>
                      <Lock className="w-4 h-4" />
                      Upgrade to unlock
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 mt-5 text-sm font-semibold text-[#0047AB]">
                      Launch simulator
                      {soon && isAdmin && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-600 ml-1" data-testid={`sim-admin-soon-note-${id}`}>
                          <Clock className="w-3.5 h-3.5" /> {etaLabel || "In development"}
                        </span>
                      )}
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  )}
                </div>
              </button>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
